import { SectorStockSignal, SectorUniverse } from '../types';

export interface SectorFilterValue {
  sector: string;
  subSector: string;
}

export const EMPTY_SECTOR_FILTER: SectorFilterValue = { sector: '', subSector: '' };

export interface StockSectorInfo {
  sector: string;
  subSector: string;
}

/** Reverse-indexes a SectorUniverse into a per-symbol sector/sub-sector lookup. */
export const buildStockSectorIndex = (sectorUniverse: SectorUniverse): Map<string, StockSectorInfo> => {
  const index = new Map<string, StockSectorInfo>();

  Object.entries(sectorUniverse ?? {}).forEach(([sector, subSectors]) => {
    Object.entries(subSectors ?? {}).forEach(([subSector, symbols]) => {
      (symbols ?? []).forEach((symbol) => {
        if (symbol && !index.has(symbol)) {
          index.set(symbol, { sector, subSector });
        }
      });
    });
  });

  return index;
};

/** Known canonical alias mappings (e.g. Energy -> Power, Defence -> Aerospace & Defense). */
export const SECTOR_CANONICAL_ALIASES: Record<string, string> = {
  energy: 'Power',
  defence: 'Aerospace & Defense',
  defense: 'Aerospace & Defense',
};

/** Returns true if two sector names are identical or recognized canonical aliases (e.g. Energy <-> Power, Defense <-> Defence). */
export const areSectorsEquivalent = (s1?: string, s2?: string): boolean => {
  if (!s1 || !s2) return false;
  const n1 = s1.trim().toLowerCase();
  const n2 = s2.trim().toLowerCase();
  if (n1 === n2) return true;
  if ((n1 === 'power' && n2 === 'energy') || (n1 === 'energy' && n2 === 'power')) return true;
  if ((n1 === 'defence' && n2.includes('defense')) || (n2 === 'defence' && n1.includes('defense'))) return true;
  return false;
};

/** Merges sector-keyed number maps (e.g. SectorTotalStocks, SectorPositiveCloseCount) by canonical alias. */
export const mergeSectorNumberMap = (map?: Record<string, number>): Record<string, number> => {
  if (!map) return {};
  const result: Record<string, number> = { ...map };
  for (const [aliasLower, canonical] of Object.entries(SECTOR_CANONICAL_ALIASES)) {
    for (const key of Object.keys(result)) {
      if (key.trim().toLowerCase() === aliasLower && key !== canonical) {
        const val = result[key] ?? 0;
        result[canonical] = Number(((result[canonical] ?? 0) + val).toFixed(2));
        delete result[key];
      }
    }
  }
  return result;
};

/** Merges sector-keyed string array maps (e.g. SectorAccumulatedStocks, SectorStocksAbove21Ema) by canonical alias. */
export const mergeSectorStringArrayMap = (map?: Record<string, string[]>): Record<string, string[]> => {
  if (!map) return {};
  const result: Record<string, string[]> = { ...map };
  for (const [aliasLower, canonical] of Object.entries(SECTOR_CANONICAL_ALIASES)) {
    for (const key of Object.keys(result)) {
      if (key.trim().toLowerCase() === aliasLower && key !== canonical) {
        const arr = result[key] ?? [];
        result[canonical] = Array.from(new Set([...(result[canonical] ?? []), ...arr]));
        delete result[key];
      }
    }
  }
  return result;
};

/** Merges sector-keyed stock signal lists by canonical alias. */
export const mergeSectorSignalsMap = (map?: Record<string, SectorStockSignal[]>): Record<string, SectorStockSignal[]> => {
  if (!map) return {};
  const result: Record<string, SectorStockSignal[]> = { ...map };
  for (const [aliasLower, canonical] of Object.entries(SECTOR_CANONICAL_ALIASES)) {
    for (const key of Object.keys(result)) {
      if (key.trim().toLowerCase() === aliasLower && key !== canonical) {
        const sigs = result[key] ?? [];
        const existing = result[canonical] ?? [];
        const seen = new Set(existing.map((s) => s.Symbol));
        const merged = [...existing];
        for (const sig of sigs) {
          if (!seen.has(sig.Symbol)) {
            seen.add(sig.Symbol);
            merged.push(sig);
          }
        }
        result[canonical] = merged;
        delete result[key];
      }
    }
  }
  return result;
};

/** Merges nested sector-keyed maps (e.g. SubSectorTotalStocks, SubSectorAbove21Ema) by canonical alias. */
export const mergeNestedSectorMap = <T>(map?: Record<string, Record<string, T>>): Record<string, Record<string, T>> => {
  if (!map) return {};
  const result: Record<string, Record<string, T>> = { ...map };
  for (const [aliasLower, canonical] of Object.entries(SECTOR_CANONICAL_ALIASES)) {
    for (const key of Object.keys(result)) {
      if (key.trim().toLowerCase() === aliasLower && key !== canonical) {
        const sub = result[key] ?? {};
        result[canonical] = {
          ...(result[canonical] ?? {}),
          ...sub,
        };
        delete result[key];
      }
    }
  }
  return result;
};

/** Returns true when a symbol matches the active sector/sub-sector filter (or when no filter is set). */
export const matchesSectorFilter = (
  symbol: string,
  stockSectorIndex: Map<string, StockSectorInfo>,
  filter: SectorFilterValue,
): boolean => {
  if (!filter.sector) {
    return true;
  }

  const info = stockSectorIndex.get(symbol);
  if (!info || !areSectorsEquivalent(info.sector, filter.sector)) {
    return false;
  }

  if (filter.subSector && info.subSector.trim().toLowerCase() !== filter.subSector.trim().toLowerCase()) {
    return false;
  }

  return true;
};
